import { CaretDownIcon, CheckIcon } from "@phosphor-icons/react";
import { NavLink } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { t } from "@/i18n/messages";
import { useLanguage } from "@/i18n/useLanguage";

const links = [
  { to: "/install", labelKey: "footer.install" },
  { to: "/privacy", labelKey: "footer.privacy" },
  { to: "/terms", labelKey: "footer.terms" },
  { to: "/impressum", labelKey: "footer.impressum" },
];

const languageOptions = [
  { value: "en", shortLabel: "EN", labelKey: "footer.english" },
  { value: "de", shortLabel: "DE", labelKey: "footer.german" },
];

export function AppFooter() {
  const { language, setLanguage } = useLanguage();
  const currentLanguage = languageOptions.find((option) => option.value === language) ?? languageOptions[0];

  return (
    <footer className="fixed inset-x-0 bottom-0 z-[1190] h-[var(--app-footer-height)] border-t border-white/10 bg-[#09090b]/95 px-3 text-[11px] text-zinc-400 shadow-[0_-8px_24px_rgba(0,0,0,.22)] backdrop-blur">
      <div className="mx-auto flex h-11 max-w-6xl items-center justify-center gap-x-4 gap-y-1 sm:justify-between">
        <p className="hidden sm:block">© {new Date().getFullYear()} Bikeparks Finder</p>
        <div className="flex items-center gap-3">
          <nav aria-label={t("footer.legalAria")}>
            <ul className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 sm:gap-x-4">
              {links.map((link) => (
                <li key={link.to}>
                  <NavLink
                    to={link.to}
                    className={({ isActive }) =>
                      `rounded-sm px-1.5 py-0.5 transition-colors hover:text-[#CCFF00] ${
                        isActive ? "bg-[#CCFF00]/10 font-semibold text-[#CCFF00]" : ""
                      }`
                    }
                  >
                    {t(link.labelKey)}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          <span className="hidden h-3 w-px bg-white/15 sm:block" aria-hidden="true" />

          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="footer-language-switcher"
                aria-label={language === "de" ? t("footer.switchToEnglish") : t("footer.switchToGerman")}
                className="flex h-7 items-center gap-1 rounded-md border border-white/10 bg-white/[0.03] px-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-400 transition-colors hover:border-white/20 hover:bg-white/[0.06] hover:text-white"
              >
                <span>{currentLanguage.shortLabel}</span>
                <CaretDownIcon size={10} weight="bold" className="text-zinc-600" aria-hidden="true" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              side="top"
              sideOffset={7}
              className="z-[1300] min-w-[8.5rem] border-white/10 bg-[#18181b] p-1 text-xs text-zinc-200"
            >
              {languageOptions.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  lang={option.value}
                  onSelect={() => setLanguage(option.value)}
                  className="flex cursor-pointer items-center justify-between gap-3 rounded-sm px-2 py-1.5 focus:bg-white/10 focus:text-white"
                >
                  <span>{t(option.labelKey)}</span>
                  {language === option.value && <CheckIcon size={13} weight="bold" className="text-[#CCFF00]" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </footer>
  );
}
